import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import {
  CreateEquipmentRequest,
  ContainerStatusResponse,
  EquipmentResponse,
  SecretsResponse,
  AuditEntryResponse,
} from '../types';

/**
 * Client for communicating with the Mattaverse Orchestration backend API.
 */
export class MattaverseOrchestrationClient {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
  ) { }

  /**
   * List all equipment entries.
   */
  async listEquipment(): Promise<EquipmentResponse[]> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/equipment`);
    return this.handleResponse<EquipmentResponse[]>(response);
  }

  /**
   * Get a single equipment entry by ID.
   */
  async getEquipment(id: string): Promise<EquipmentResponse> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/equipment/${id}`);
    return this.handleResponse<EquipmentResponse>(response);
  }

  /**
   * Create a new equipment entry. Certificate and private key contents
   * must be provided as base64-encoded strings.
   */
  async createEquipment(
    request: CreateEquipmentRequest,
  ): Promise<EquipmentResponse> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/equipment`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });
    return this.handleResponse<EquipmentResponse>(response);
  }

  /**
   * Update the status (stopped/running) of an equipment entry.
   */
  async updateStatus(
    id: string,
    status: 'stopped' | 'running',
  ): Promise<EquipmentResponse> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/equipment/${id}/status`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      },
    );
    return this.handleResponse<EquipmentResponse>(response);
  }

  /**
   * Delete an equipment entry.
   */
  async deleteEquipment(id: string): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/equipment/${id}`, {
      method: 'DELETE',
    });
    await this.handleResponse<void>(response);
  }

  /**
   * Retrieve the base64-encoded certificate and private key for an
   * equipment entry.
   */
  async getSecrets(id: string): Promise<SecretsResponse> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/equipment/${id}/secrets`,
    );
    return this.handleResponse<SecretsResponse>(response);
  }

  /**
   * Trigger a container start for the given equipment entry.
   */
  async startEquipment(id: string): Promise<EquipmentResponse> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/equipment/${id}/start`,
      { method: 'POST' },
    );
    return this.handleResponse<EquipmentResponse>(response);
  }

  /**
   * Trigger a container stop for the given equipment entry.
   */
  async stopEquipment(id: string): Promise<EquipmentResponse> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/equipment/${id}/stop`,
      { method: 'POST' },
    );
    return this.handleResponse<EquipmentResponse>(response);
  }

  /**
   * Get the current container status for polling.
   */
  async getContainerStatus(id: string): Promise<ContainerStatusResponse> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/equipment/${id}/container-status`,
    );
    return this.handleResponse<ContainerStatusResponse>(response);
  }

  /**
   * Retrieve the lifecycle audit trail (created/started/stopped) for an entry.
   */
  async getAuditLog(id: string): Promise<AuditEntryResponse[]> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/equipment/${id}/audit`,
    );
    return this.handleResponse<AuditEntryResponse[]>(response);
  }

  /**
   * Issues a Backstage user cookie so the browser can access the
   * container UI proxy route in a new tab without a Bearer token.
   */
  async issueUiCookie(id: string): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}/equipment/${id}/ui-cookie`,
      { method: 'POST', credentials: 'include' },
    );
    await this.handleResponse<void>(response);
  }

  /**
   * Returns the URL to open the container UI for a given equipment entry.
   *
   * A fresh `session` token is appended on every call so each opened link is
   * unique. The token lives only in the browser address bar (the backend
   * ignores it), so refreshing the tab still resolves to the same container.
   */
  async getUiUrl(id: string): Promise<string> {
    const baseUrl = await this.getBaseUrl();
    const session = crypto.randomUUID();
    return `${baseUrl}/equipment/${id}/ui/?session=${session}`;
  }

  private async getBaseUrl(): Promise<string> {
    return this.discoveryApi.getBaseUrl('mattaverse-orchestration');
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const text = await response.text();
      let errorMessage: string;
      try {
        const errorBody = JSON.parse(text);
        if (errorBody?.error?.name && errorBody?.error?.message) {
          errorMessage = `${errorBody.error.name}: ${errorBody.error.message}`;
        } else {
          errorMessage = `HTTP ${response.status}: ${text}`;
        }
      } catch {
        errorMessage = `HTTP ${response.status}: ${text}`;
      }
      throw new Error(errorMessage);
    }

    // For 204 No Content (DELETE responses)
    if (response.status === 204) {
      return undefined as T;
    }

    return response.json() as Promise<T>;
  }
}
